// Game API wrapper - Bridge to Supabase backend with 50 Edge Functions
import SupabaseAPI, { 
  ApiResponse, 
  Player, 
  Fleet, 
  Planet, 
  Research, 
  Alliance, 
  Market 
} from './SupabaseAPI'

class GameAPI {
  private static instance: GameAPI
  private supabaseAPI: SupabaseAPI
  
  constructor() {
    this.supabaseAPI = SupabaseAPI.getInstance()
  }
  
  static getInstance(): GameAPI {
    if (!GameAPI.instance) {
      GameAPI.instance = new GameAPI()
    }
    return GameAPI.instance
  }

  // ===========================================
  // AUTHENTICATION MANAGEMENT
  // ===========================================
  
  setToken(token: string | null): void {
    this.supabaseAPI.setToken(token)
  }

  getToken(): string | null {
    return this.supabaseAPI.getToken()
  }

  // ===========================================
  // PLAYER MANAGEMENT
  // ===========================================
  
  async getPlayer(playerId: string): Promise<ApiResponse<Player>> {
    return this.supabaseAPI.getPlayer(playerId)
  }

  async getPlayers(): Promise<ApiResponse<Player[]>> {
    return this.supabaseAPI.getPlayers()
  }

  async getProfile(): Promise<ApiResponse<Player>> {
    // Get current user's profile
    const userResult = await this.getCurrentUser()
    if (userResult.status !== 200 || !userResult.data?.id) {
      return { status: 401, error: 'No authenticated user found' }
    }
    return this.getPlayer(userResult.data.id)
  }

  async updateProfile(profileData: Partial<Player>): Promise<ApiResponse<Player>> {
    // Update current user's profile
    const userResult = await this.getCurrentUser()
    if (userResult.status !== 200 || !userResult.data?.id) {
      return { status: 401, error: 'No authenticated user found' }
    }
    return this.updatePlayer(userResult.data.id, profileData)
  }

  async createPlayer(playerData: Partial<Player>): Promise<ApiResponse<Player>> {
    return this.supabaseAPI.createPlayer(playerData)
  }

  async updatePlayer(playerId: string, playerData: Partial<Player>): Promise<ApiResponse<Player>> {
    return this.supabaseAPI.updatePlayer(playerId, playerData)
  }

  async deletePlayer(playerId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.supabaseAPI.deletePlayer(playerId)
  }

  // ===========================================
  // FLEET MANAGEMENT
  // ===========================================

  async getFleets(playerId?: string): Promise<ApiResponse<Fleet[]>> {
    return this.supabaseAPI.getFleets(playerId)
  }

  async getFleet(fleetId: string): Promise<ApiResponse<Fleet>> {
    return this.supabaseAPI.getFleet(fleetId)
  }

  async createFleet(fleetData: Partial<Fleet>): Promise<ApiResponse<Fleet>> {
    return this.supabaseAPI.createFleet(fleetData)
  }

  async updateFleet(fleetId: string, fleetData: Partial<Fleet>): Promise<ApiResponse<Fleet>> {
    return this.supabaseAPI.updateFleet(fleetId, fleetData)
  }

  async deleteFleet(fleetId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.supabaseAPI.deleteFleet(fleetId)
  }

  // ===========================================
  // PLANET MANAGEMENT
  // ===========================================

  async getPlanets(playerId?: string): Promise<ApiResponse<Planet[]>> {
    return this.supabaseAPI.getPlanets(playerId)
  }

  async getPlanet(planetId: string): Promise<ApiResponse<Planet>> {
    return this.supabaseAPI.getPlanet(planetId)
  }

  async createPlanet(planetData: Partial<Planet>): Promise<ApiResponse<Planet>> {
    return this.supabaseAPI.createPlanet(planetData)
  }

  async updatePlanet(planetId: string, planetData: Partial<Planet>): Promise<ApiResponse<Planet>> {
    return this.supabaseAPI.updatePlanet(planetId, planetData)
  }

  async deletePlanet(planetId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.supabaseAPI.deletePlanet(planetId)
  }

  // ===========================================
  // RESEARCH MANAGEMENT
  // ===========================================

  async getResearch(playerId: string): Promise<ApiResponse<Research[]>> {
    return this.supabaseAPI.getResearch(playerId)
  }

  async getResearchItem(researchId: string): Promise<ApiResponse<Research>> {
    return this.supabaseAPI.getResearchItem(researchId)
  }

  async createResearch(researchData: Partial<Research>): Promise<ApiResponse<Research>> {
    return this.supabaseAPI.createResearch(researchData)
  }

  async updateResearch(researchId: string, researchData: Partial<Research>): Promise<ApiResponse<Research>> {
    return this.supabaseAPI.updateResearch(researchId, researchData)
  }

  async deleteResearch(researchId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.supabaseAPI.deleteResearch(researchId)
  }

  // ===========================================
  // ALLIANCE MANAGEMENT
  // ===========================================

  async getAlliances(): Promise<ApiResponse<Alliance[]>> {
    return this.supabaseAPI.getAlliances()
  }

  async getAlliance(allianceId: string): Promise<ApiResponse<Alliance>> {
    return this.supabaseAPI.getAlliance(allianceId)
  }

  async createAlliance(allianceData: Partial<Alliance>): Promise<ApiResponse<Alliance>> {
    return this.supabaseAPI.createAlliance(allianceData)
  }

  async updateAlliance(allianceId: string, allianceData: Partial<Alliance>): Promise<ApiResponse<Alliance>> {
    return this.supabaseAPI.updateAlliance(allianceId, allianceData)
  }

  async deleteAlliance(allianceId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.supabaseAPI.deleteAlliance(allianceId)
  }

  // ===========================================
  // MARKET MANAGEMENT
  // ===========================================

  async getMarket(): Promise<ApiResponse<Market[]>> {
    return this.supabaseAPI.getMarket()
  }

  async getMarketItem(itemId: string): Promise<ApiResponse<Market>> {
    return this.supabaseAPI.getMarketItem(itemId)
  }

  async createMarketItem(itemData: Partial<Market>): Promise<ApiResponse<Market>> {
    return this.supabaseAPI.createMarketItem(itemData)
  }

  async updateMarketItem(itemId: string, itemData: Partial<Market>): Promise<ApiResponse<Market>> {
    return this.supabaseAPI.updateMarketItem(itemId, itemData)
  }

  async deleteMarketItem(itemId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.supabaseAPI.deleteMarketItem(itemId)
  }

  // ===========================================
  // GAME SYSTEMS
  // ===========================================

  // Battle System
  async getBattles(playerId?: string): Promise<ApiResponse<any[]>> {
    return this.supabaseAPI.getBattles(playerId)
  }

  async createBattle(battleData: any): Promise<ApiResponse<any>> {
    return this.supabaseAPI.createBattle(battleData)
  }

  // Diplomacy System
  async getDiplomacy(playerId: string): Promise<ApiResponse<any[]>> {
    return this.supabaseAPI.getDiplomacy(playerId)
  }

  async createDiplomacy(diplomacyData: any): Promise<ApiResponse<any>> {
    return this.supabaseAPI.createDiplomacy(diplomacyData)
  }

  // Trade System
  async getTrades(playerId?: string): Promise<ApiResponse<any[]>> {
    return this.supabaseAPI.getTrades(playerId)
  }

  async createTrade(tradeData: any): Promise<ApiResponse<any>> {
    return this.supabaseAPI.createTrade(tradeData)
  }

  // Communication System
  async getMessages(playerId: string): Promise<ApiResponse<any[]>> {
    return this.supabaseAPI.getMessages(playerId)
  }

  async sendMessage(messageData: any): Promise<ApiResponse<any>> {
    return this.supabaseAPI.sendMessage(messageData)
  }

  // Notification System
  async getNotifications(playerId: string): Promise<ApiResponse<any[]>> {
    return this.supabaseAPI.getNotifications(playerId)
  }

  async createNotification(notificationData: any): Promise<ApiResponse<any>> {
    return this.supabaseAPI.createNotification(notificationData)
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse<any>> {
    return this.supabaseAPI.updateNotification(notificationId, { read: true })
  }

  async trackEvent(eventName: string, eventData: any): Promise<ApiResponse<any>> {
    console.log('Event tracked:', eventName, eventData)
    // For now, just log the event. In production, this would send to analytics service
    return { status: 200, data: { eventName, eventData, timestamp: new Date().toISOString() } }
  }

  // Analytics & Reporting
  async getAnalytics(playerId: string): Promise<ApiResponse<any>> {
    return this.supabaseAPI.getAnalytics(playerId)
  }

  async getReports(): Promise<ApiResponse<any[]>> {
    return this.supabaseAPI.getReports()
  }

  // Page Settings Integration
  async getPageSettings(): Promise<ApiResponse<any>> {
    return this.supabaseAPI.getPageSettings()
  }

  async updatePageSettings(settings: any): Promise<ApiResponse<any>> {
    return this.supabaseAPI.updatePageSettings(settings)
  }

  // Health & Monitoring
  async healthCheck(): Promise<ApiResponse<{ status: string, timestamp: string }>> {
    return this.supabaseAPI.healthCheck()
  }

  async getSystemStatus(): Promise<ApiResponse<any>> {
    return this.supabaseAPI.getSystemStatus()
  }

  // Authentication
  async login(credentials: { email: string, password: string }): Promise<ApiResponse<any>> {
    return this.supabaseAPI.login(credentials)
  }

  async logout(): Promise<ApiResponse<{ success: boolean }>> {
    return this.supabaseAPI.logout()
  }

  async getCurrentUser(): Promise<ApiResponse<any>> {
    return this.supabaseAPI.getCurrentUser()
  }

  // Real-time subscriptions
  subscribeToChanges(table: string, callback: (payload: any) => void) {
    return this.supabaseAPI.subscribeToChanges(table, callback)
  }

  unsubscribe(subscription: any) {
    return this.supabaseAPI.unsubscribe(subscription)
  }

  // Legacy compatibility methods for existing code
  async getKVValue(_key: string): Promise<ApiResponse<any>> {
    console.warn('getKVValue is deprecated, use page settings or game-specific endpoints')
    return this.getPageSettings()
  }

  async setKVValue(key: string, value: any): Promise<ApiResponse<{ success: boolean }>> {
    console.warn('setKVValue is deprecated, use page settings or game-specific endpoints')
    return this.updatePageSettings({ [key]: value })
  }
}

// Export singleton instance for easy access
const gameAPI = GameAPI.getInstance()
export default gameAPI

// Re-export types from SupabaseAPI for compatibility
export type { ApiResponse, Player, Fleet, Planet, Research, Alliance, Market } from './SupabaseAPI'